import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { SelectModule } from 'primeng/select';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { DividerModule } from 'primeng/divider';
import { MessageModule } from 'primeng/message';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { countries, countryCodes } from './utils/countries-list';
import { UserService } from '../../services/user.service';
import { environment } from '../../../environment/environment';

@Component({
  selector: 'app-signup-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    SelectModule,
    FloatLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    DividerModule,
    MessageModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './signup-page.component.html',
  styleUrls: ['./signup-page.component.scss']
})
export class SignupPageComponent {
  signupForm: FormGroup;
  
  private static readonly PROD_DISABLED_ROLES = ['software-developer'];

  userTypes = [
    { label: 'Technician', value: 'technician' },
    { label: 'SW Developer', value: 'software-developer' },
    { label: 'Technician Admin', value: 'technician-admin' },
    { label: 'SW Manager', value: 'software-manager' }
  ].filter(type => !environment.production || !SignupPageComponent.PROD_DISABLED_ROLES.includes(type.value));

  countries = countries;
  loading = false;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private userService: UserService,
    private messageService: MessageService
  ) {
    this.signupForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, this.passwordValidator]],
      confirmPassword: ['', [Validators.required]],
      userType: ['', [Validators.required]],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      phoneNumber: ['', [Validators.required, this.phoneNumberValidator]],
      address: ['', [Validators.required]],
      country: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });

    this.signupForm.get('country')?.valueChanges.subscribe(country => {
      this.onCountryChange(country);
    });
  }

  passwordValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumeric = /[0-9]/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
    const isValidLength = value.length >= 8;

    const passwordValid = hasUpperCase && hasLowerCase && hasNumeric && hasSpecialChar && isValidLength;

    if (!passwordValid) {
      return {
        passwordStrength: {
          hasUpperCase,
          hasLowerCase,
          hasNumeric,
          hasSpecialChar,
          isValidLength
        }
      };
    }

    return null;
  }

  phoneNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }
    const e164Pattern = /^\+[1-9]\d{1,14}$/;
    if (!e164Pattern.test(value)) {
      return {
        phoneNumber: {
          message: 'Phone number must include country code (example +39)'
        }
      };
    }
    return null;
  }

  passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password');
    const confirmPassword = group.get('confirmPassword');

    if (!password || !confirmPassword) {
      return null;
    }

    return password.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  onSignup(): void {
    if (this.signupForm.valid) {
      this.loading = true;
      const formValue = this.signupForm.value;
      const userData = {
        email: formValue.email,
        password: formValue.password,
        name: formValue.firstName,
        surname: formValue.lastName,
        userType: formValue.userType,
        phoneNumber: formValue.phoneNumber,
        address: formValue.address,
        country: formValue.country
      };

      this.userService.createUser(userData).subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'User request sent'
          });
          this.loading = false;
          this.router.navigate(['/registration-submitted']);
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.error?.message || 'Failed to create user. Please try again.'
          });
          this.loading = false;
        }
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields correctly.'
      });
    }
  }

  goToSignin(): void {
    this.router.navigate(['/signin']);
  }

  isFormValid(): boolean {
    return this.signupForm.valid;
  }

  getPasswordErrors(): string[] {
    const errors = [];
    const passwordControl = this.signupForm.get('password');
    
    if (passwordControl?.errors?.['passwordStrength']) {
      const strength = passwordControl.errors['passwordStrength'];
      if (!strength.isValidLength) errors.push('At least 8 characters');
      if (!strength.hasUpperCase) errors.push('At least 1 uppercase letter');
      if (!strength.hasLowerCase) errors.push('At least 1 lowercase letter');
      if (!strength.hasNumeric) errors.push('At least 1 number');
      if (!strength.hasSpecialChar) errors.push('At least 1 special character');
    }
    
    return errors;
  }

  getPhoneNumberError(): string | null {
    const phoneControl = this.signupForm.get('phoneNumber');
    if (phoneControl?.errors?.['phoneNumber'] && phoneControl.touched) {
      return phoneControl.errors['phoneNumber'].message;
    }
    if (phoneControl?.errors?.['required'] && phoneControl.touched) {
      return 'Phone number is required';
    }
    return null;
  }

  hasPasswordMismatch(): boolean {
    return this.signupForm.hasError('passwordMismatch') && 
           this.signupForm.get('confirmPassword')?.touched === true;
  }

  onCountryChange(country: string): void {
    if (!country) return;
    const countryCode = countryCodes[country];
    if (!countryCode) return;
    const phoneControl = this.signupForm.get('phoneNumber');
    const currentPhone = phoneControl?.value || '';
    if (currentPhone && currentPhone.startsWith('+')) {
      return;
    }
    if (currentPhone) {
      const cleanPhone = currentPhone.replace(/^0+/, '').replace(/^\D+/, '');
      if (cleanPhone) {
        phoneControl?.setValue(`${countryCode}${cleanPhone}`);
      }
    } else {
      phoneControl?.setValue(countryCode);
    }
  }

  onPhoneNumberInput(): void {
    const phoneControl = this.signupForm.get('phoneNumber');
    const currentPhone = phoneControl?.value || '';
    const selectedCountry = this.signupForm.get('country')?.value;

    if (selectedCountry && currentPhone && !currentPhone.startsWith('+') && /^\d/.test(currentPhone)) {
      const countryCode = countryCodes[selectedCountry];
      if (countryCode) {
        const cleanPhone = currentPhone.replace(/^0+/, '');
        phoneControl?.setValue(`${countryCode}${cleanPhone}`);
      }
    }
  }
}
